import { type ReactNode } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import {
  ArrowRight,
  ShieldCheck,
  Sparkles,
  ScrollText,
  Lock,
  Database,
  CheckCircle2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { PublicShell } from '@/components/PublicShell';
import { useAuth } from '@/lib/auth';
import { cn, formatAud } from '@/lib/utils';

export const Route = createFileRoute('/')({
  component: Landing,
});

function Landing() {
  return (
    <PublicShell>
      <Hero />
      <Reassurance />
      <ValueGrid />
      <ClosingCard />
    </PublicShell>
  );
}

function Eyebrow({ children }: { children: ReactNode }) {
  return (
    <div className="text-[12px] font-bold uppercase tracking-[0.08em] text-(--color-navy)">
      {children}
    </div>
  );
}

function Hero() {
  return (
    <section className="border-b border-(--color-rule)">
      <div className="mx-auto grid max-w-6xl items-center gap-12 px-6 py-16 lg:grid-cols-[1.1fr_1fr] lg:py-24">
        <div className="space-y-6">
          <Eyebrow>FY 2025–26 lodgement</Eyebrow>
          <h1 className="text-5xl font-extrabold leading-[1.05] tracking-[-0.02em] text-(--color-ink) md:text-6xl [text-wrap:balance]">
            Australian tax returns,
            <br />
            <span className="text-(--color-navy)">without the dread.</span>
          </h1>
          <p className="max-w-xl text-[17px] leading-relaxed text-(--color-ink-2)">
            A guided portal with an AI assistant that pre-fills from the ATO and calculates your
            deductions correctly. No tax-time panic. No spreadsheets.
          </p>
          <HeroCtas />
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 pt-1 text-[13px] font-semibold text-(--color-ink-muted)">
            <span className="inline-flex items-center gap-1.5">
              <ShieldCheck className="size-4 text-(--color-navy)" />
              TFN encrypted at rest
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Database className="size-4 text-(--color-navy)" />
              Direct ATO pre-fill
            </span>
            <span className="inline-flex items-center gap-1.5">
              <CheckCircle2 className="size-4 text-(--color-navy)" />
              Reviewer-checked
            </span>
          </div>
        </div>

        <ReceiptCard />
      </div>
    </section>
  );
}

function ReceiptCard() {
  const totalIncome = 92480;
  const totalDeductions = 3260;
  const taxable = totalIncome - totalDeductions;
  const withheld = 21340;
  const owed = 18120;
  const refund = withheld - owed;

  return (
    <div className="rounded-(--radius-lg) border border-(--color-rule) bg-(--color-card) p-6 shadow-[0_10px_28px_rgb(20_35_61_/_0.08)]">
      <div className="flex items-center justify-between border-b border-(--color-rule) pb-3">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.08em] text-(--color-ink-muted)">
            Sample summary
          </div>
          <div className="mt-1 text-lg font-extrabold text-(--color-ink)">FY 2025–26</div>
        </div>
        <span className="rounded-full bg-(--color-navy-soft) px-2.5 py-1 text-[11px] font-bold text-(--color-navy)">
          Preview
        </span>
      </div>
      <dl className="mt-4 space-y-3 text-[14px]">
        <Line label="Salary & wages" value={84000} />
        <Line label="Interest" value={6280} />
        <Line label="Dividends" value={2200} />
        <div className="border-t border-(--color-rule)" aria-hidden />
        <Line label="Total income" value={totalIncome} bold />
        <Line label="Work-from-home (1,150 hrs)" value={-805} muted />
        <Line label="Donations" value={-2455} muted />
        <div className="border-t border-(--color-rule)" aria-hidden />
        <Line label="Taxable income" value={taxable} bold />
        <Line label="Tax + Medicare levy" value={-owed} muted />
        <Line label="PAYG already withheld" value={withheld} muted />
      </dl>
      <div className="mt-4 flex items-baseline justify-between border-t-[1.5px] border-(--color-rule-strong) pt-4">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.08em] text-(--color-ink-muted)">
            Estimated refund
          </div>
          <div className="mt-0.5 text-[12px] text-(--color-ink-muted)">Direct to your bank.</div>
        </div>
        <div className="text-3xl font-extrabold tabular-nums text-(--color-navy)">
          +{formatAud(refund)}
        </div>
      </div>
    </div>
  );
}

function Line({
  label,
  value,
  bold,
  muted,
}: {
  label: string;
  value: number;
  bold?: boolean;
  muted?: boolean;
}) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className={muted ? 'text-(--color-ink-muted)' : 'text-(--color-ink-2)'}>{label}</dt>
      <dd
        className={cn(
          'tabular-nums',
          bold ? 'font-bold text-(--color-ink)' : muted ? 'text-(--color-ink-muted)' : 'text-(--color-ink-2)',
        )}
      >
        {formatAud(value)}
      </dd>
    </div>
  );
}

function Reassurance() {
  return (
    <section className="border-b border-(--color-rule) bg-(--color-card)">
      <div className="mx-auto max-w-4xl px-6 py-16 text-center">
        <Eyebrow>Why people switch</Eyebrow>
        <p className="mx-auto mt-5 max-w-3xl text-[22px] font-semibold leading-snug tracking-[-0.01em] text-(--color-ink) md:text-[26px] [text-wrap:balance]">
          “I used to put off doing my return for weeks. With NightTax, the assistant pulled
          everything from the ATO, suggested the deductions I'd missed, and a real reviewer signed
          off before I lodged. Done in twenty minutes.”
        </p>
        <div className="mt-6 text-[13px] font-medium text-(--color-ink-muted)">
          Anonymised customer · Sydney · FY 2024–25
        </div>
      </div>
    </section>
  );
}

function ValueGrid() {
  const items = [
    {
      icon: Database,
      title: 'Pre-fill from the ATO',
      body: 'Your assistant brings every PAYG, dividend, interest, and government-payment statement into your return automatically — no paperwork to dig up.',
    },
    {
      icon: Sparkles,
      title: 'Right-sized deductions',
      body: "Cents-per-km, fixed-rate WFH, occupation-specific claims — the assistant uses this year's ATO rules and explains every figure in plain English.",
    },
    {
      icon: ScrollText,
      title: 'See your refund as you go',
      body: 'Your estimated refund updates the moment you add income or a deduction — with a line-by-line breakdown of exactly how it was worked out.',
    },
    {
      icon: ShieldCheck,
      title: 'Audit-logged & encrypted',
      body: 'Every change is recorded with timestamps — by you or the assistant. Your TFN and signed declaration are encrypted at rest. Nothing leaves Australia.',
    },
  ];
  return (
    <section className="border-b border-(--color-rule)">
      <div className="mx-auto max-w-6xl px-6 py-16">
        <div className="max-w-2xl">
          <Eyebrow>What you get</Eyebrow>
          <h2 className="mt-2 text-3xl font-extrabold tracking-[-0.01em] text-(--color-ink) md:text-4xl">
            Calm at every step.
          </h2>
        </div>
        <div className="mt-10 grid gap-4 md:grid-cols-2">
          {items.map(({ icon: Icon, title, body }) => (
            <article
              key={title}
              className="rounded-(--radius-lg) border border-(--color-rule) bg-(--color-card) p-6 shadow-[0_1px_2px_rgb(20_35_61_/_0.05)]"
            >
              <span className="flex size-11 items-center justify-center rounded-(--radius) bg-(--color-navy-soft) text-(--color-navy)">
                <Icon className="size-5" strokeWidth={1.8} />
              </span>
              <h3 className="mt-4 text-[17px] font-bold text-(--color-ink)">{title}</h3>
              <p className="mt-1.5 text-[14px] leading-relaxed text-(--color-ink-2)">{body}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function ClosingCard() {
  return (
    <section className="mx-auto max-w-6xl px-6 py-16">
      <div
        className="overflow-hidden rounded-(--radius-lg) px-8 py-12 text-white shadow-[0_10px_28px_rgb(29_95_214_/_0.22)] lg:px-14 lg:py-14"
        style={{ background: 'linear-gradient(140deg,#1d5fd6,#2f74ea)' }}
      >
        <div className="grid items-center gap-8 lg:grid-cols-[1.2fr_1fr]">
          <div className="space-y-4">
            <div className="text-[12px] font-bold uppercase tracking-[0.1em] text-white/80">
              Lodgement closes 31 October
            </div>
            <h2 className="text-3xl font-extrabold leading-tight tracking-[-0.01em] text-white md:text-4xl [text-wrap:balance]">
              Twenty quiet minutes, and you're done for the year.
            </h2>
            <p className="max-w-md text-[15px] leading-relaxed text-white/85">
              Sign in, link your ATO record, and let the assistant draft your return. You review and
              sign — that's it.
            </p>
          </div>
          <ClosingCtas />
        </div>
      </div>
    </section>
  );
}

function HeroCtas() {
  const { data: user } = useAuth();
  return (
    <div className="flex flex-wrap items-center gap-3 pt-1">
      <Button asChild size="lg">
        <Link to="/returns">
          {user ? 'Continue your return' : 'Start your return'} <ArrowRight className="size-4" />
        </Link>
      </Button>
      {!user && (
        <Button asChild variant="secondary" size="lg">
          <Link to="/login">
            <Lock className="size-4" />
            Sign in
          </Link>
        </Button>
      )}
    </div>
  );
}

function ClosingCtas() {
  const { data: user } = useAuth();
  return (
    <div className="flex flex-wrap items-center gap-3 lg:justify-end">
      <Link
        to="/returns"
        className="inline-flex items-center gap-2 rounded-(--radius) bg-white px-5 py-3 text-[15px] font-bold text-(--color-navy) transition-[filter] hover:brightness-95"
      >
        {user ? 'Continue your return' : 'Start your return'}
        <ArrowRight className="size-4" />
      </Link>
      {!user && (
        <Link
          to="/login"
          className="inline-flex items-center gap-2 rounded-(--radius) border border-white/40 px-5 py-3 text-[15px] font-bold text-white hover:bg-white/10"
        >
          <Lock className="size-4" />
          I have an account
        </Link>
      )}
    </div>
  );
}
